import LoadingButton from '@mui/lab/LoadingButton';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Button, Card, Stack, Typography, type SxProps } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import RHFEditor from 'src/components/hook-form/rhf-editor';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import Iconify from 'src/components/iconify';
import { useMyDataStore } from 'src/store/me/me.store';

type Props = {
  methods: any;
  onSubmit: VoidFunction;
  isEdit: boolean;
  isSubmitting: boolean;
  statusOptions: string[];
  statusLabel: string[];
  privateOptions: boolean[];
  privateLabel: string[];
  values: any;
  control: any;
  isValidEndDate: boolean;
  isPrivate?: boolean | string;
  handleDrop: (acceptedFiles: File[]) => void;
  dontLabel: string[];
  dontOptions: boolean[];
  cpfs?: string[];
  setOpenCpfs: any;
  hasCpfsToDownload: boolean;
  handleDownloadCpfs: VoidFunction;
  activeFreeCollectiblesCount: number;
  isLoadingCollectionPeriods: boolean;
  hasUnavailableCollectionDateRangeOverlap: boolean;
  shouldDisableCollectionDate: (date: Date, field: 'startDate' | 'endDate') => boolean;
};

const collectionDateDaySx: SxProps<Theme> = {
  position: 'relative',
  '&.MuiPickersDay-root.Mui-selected': {
    backgroundColor: 'primary.main',
  },
  '&.Mui-disabled': {
    opacity: 1,
    color: 'text.disabled',
    bgcolor: (theme) => alpha(theme.palette.error.main, 0.06),
    '&::after': {
      content: '"×"',
      position: 'absolute',
      top: 1,
      right: 5,
      fontSize: 14,
      fontWeight: 900,
      lineHeight: 1,
      color: (theme) => alpha(theme.palette.error.main, 0.78),
      pointerEvents: 'none',
    },
  },
};

function UnlockRequirementNotice({
  activeFreeCollectiblesCount,
}: Pick<Props, 'activeFreeCollectiblesCount'>) {
  if (activeFreeCollectiblesCount > 1) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ px: 1.5 }}>
        Informe um valor entre 0 e {activeFreeCollectiblesCount}, conforme os colecionáveis livres e
        ativos.
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.25,
        py: 0.9,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'warning.light',
        bgcolor: 'warning.lighter',
        color: 'warning.darker',
      }}
    >
      <WarningAmberRoundedIcon sx={{ fontSize: 19, color: 'warning.dark', flexShrink: 0 }} />
      <Typography variant="caption" fontWeight={600}>
        {activeFreeCollectiblesCount === 0
          ? 'Não há colecionáveis livres e ativos. Neste caso, o valor deve ser 0.'
          : 'Há somente 1 colecionável livre e ativo. O valor pode ser 0 ou 1.'}
      </Typography>
    </Stack>
  );
}

export default function CollectionForm({
  methods,
  onSubmit,
  isEdit,
  isSubmitting,
  statusLabel,
  statusOptions,
  privateLabel,
  privateOptions,
  values,
  control,
  isPrivate,
  isValidEndDate,
  dontLabel,
  dontOptions,
  cpfs,
  handleDrop,
  setOpenCpfs,
  hasCpfsToDownload,
  handleDownloadCpfs,
  activeFreeCollectiblesCount,
  isLoadingCollectionPeriods,
  hasUnavailableCollectionDateRangeOverlap,
  shouldDisableCollectionDate,
}: Props) {
  const permission = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit} noValidate>
      <Card>
        <Stack spacing={2} p={4}>
          <RHFTextField name="name" label="Nome da Coleção" />
          <RHFTextField name="title" label="Título" />
          <Typography variant="body1" fontWeight="bold">
            Imagem da coleção:
          </Typography>
          <RHFUploadSingleFileAccelerator
            name="image"
            acceptAll={false}
            // @ts-ignore
            onDrop={handleDrop}
          />
          <Stack>
            <Stack direction="row" spacing={2}>
              <Controller
                name="startDate"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    sx={{ flex: 1 }}
                    closeOnSelect
                    disabled={isLoadingCollectionPeriods}
                    label="Data de Início"
                    shouldDisableDate={(date) => shouldDisableCollectionDate(date, 'startDate')}
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                      day: {
                        sx: collectionDateDaySx,
                      },
                    }}
                    value={field.value}
                    onChange={(date) => {
                      if (date) {
                        const normalizedDate = new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate()
                        );
                        field.onChange(normalizedDate);
                      } else {
                        field.onChange(null);
                      }
                    }}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    sx={{ flex: 1 }}
                    closeOnSelect
                    disabled={isLoadingCollectionPeriods}
                    label="Data de Fim"
                    minDate={values.startDate || undefined}
                    shouldDisableDate={(date) => shouldDisableCollectionDate(date, 'endDate')}
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                      day: {
                        sx: collectionDateDaySx,
                      },
                    }}
                    value={field.value}
                    onChange={(date) => {
                      if (date) {
                        const normalizedDate = new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate()
                        );
                        field.onChange(normalizedDate);
                      } else {
                        field.onChange(null);
                      }
                    }}
                  />
                )}
              />
            </Stack>
            {!isValidEndDate && (
              <Typography
                sx={{
                  pl: 1.8,
                  pt: 1,
                }}
                variant="caption"
                color="error"
              >
                Data de término deve ser maior ou igual do que a data de início
              </Typography>
            )}
            {hasUnavailableCollectionDateRangeOverlap && (
              <Typography sx={{ pl: 1.8, pt: 1 }} variant="caption" color="error">
                Período selecionado cruza uma coleção existente
              </Typography>
            )}
          </Stack>
          <RHFEditor name="rules" label="Regras" />
          {isEdit && (
            <Stack spacing={0.75}>
              <RHFTextField
                name="freeRedemptionsRequiredToUnlock"
                type="number"
                label="Quantidade de resgates para o desbloqueio"
              />
              <UnlockRequirementNotice activeFreeCollectiblesCount={activeFreeCollectiblesCount} />
            </Stack>
          )}
          <Stack>
            <Typography variant="body1" fontWeight="bold">
              Tipo de exibição:
            </Typography>
            <RHFRadioGroupLegacy
              name="private"
              options={privateOptions}
              getOptionLabel={privateLabel}
            />
          </Stack>
          {(isPrivate === 'true' || isPrivate === true) && (
            <Stack spacing={2}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
                spacing={2}
              >
                <Button variant="soft" onClick={setOpenCpfs}>
                  Cadastrar Cpfs
                </Button>
                {hasCpfsToDownload && (
                  <Button
                    variant="soft"
                    startIcon={<Iconify icon="solar:download-linear" width={18} />}
                    onClick={handleDownloadCpfs}
                  >
                    Baixar planilha
                  </Button>
                )}
                <Typography variant="overline">{cpfs?.length || 0} Cpfs Cadastrados</Typography>
              </Stack>
              <Stack>
                <Typography variant="body1" fontWeight="bold">
                  Não mostrar para:
                </Typography>
                <RHFRadioGroupLegacy
                  name="dontShowToImported"
                  options={dontOptions}
                  getOptionLabel={dontLabel}
                />
              </Stack>
            </Stack>
          )}
          <Stack>
            <Typography>Status:</Typography>
            <RHFRadioGroupLegacy
              name="status"
              options={statusOptions}
              getOptionLabel={statusLabel}
            />
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={(isEdit && !permission?.update) || (!isEdit && !permission?.create)}
            >
              {!isEdit ? 'Cadastrar Coleção' : 'Editar Detalhes da Coleção'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>
    </FormProvider>
  );
}
